import React from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, MoreHorizontal, MoreVerticalIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ContactsPaginationProps {
  currentPage: number
  totalPages: number
  totalCount: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  isLoading?: boolean
  itemLabel?: string // Optional label for items (defaults to "contacts")
  pageSizeOptions?: number[] // Optional custom page size options
  minPageSize?: number // Optional minimum page size
  hideTotalCount?: boolean // Hide the "of X" total count (useful for cursor-based APIs)
  /**
   * Hide the "Rows per page" select. For surfaces with a FIXED page size (e.g.
   * a card grid sized to its column count), where the control would be a
   * single-option stub. Defaults to false so every existing consumer is
   * unaffected. When true, `onPageSizeChange` is never called.
   */
  hidePageSizeSelector?: boolean
}

export function ContactsPagination({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  itemLabel = 'results',
  pageSizeOptions = [10, 20],
  minPageSize = 10,
  hideTotalCount = false,
  hidePageSizeSelector = false
}: ContactsPaginationProps) {
  const startItem = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0
  const endItem = Math.min(currentPage * pageSize, totalCount)

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page)
    }
  }

  const handlePageSizeChange = (value: string) => {
    const newSize = parseInt(value)
    const finalSize = minPageSize ? Math.max(newSize, minPageSize) : newSize
    onPageSizeChange(finalSize)
  }

  if (totalPages <= 1 && totalCount <= pageSize) {
    if (totalCount === 0) return null;
    // Still show if we have items but only one page? 
    // DataTable shows pagination if effectiveShowPagination is true.
    // Let's keep it consistent.
  }

  return (
    <div className="flex flex-col lg:flex-row items-center justify-between gap-4 px-4 py-4 bg-white rounded-b-lg border-t w-full overflow-x-auto">
      {/* Left side: Results count and Rows per page combined */}
      <div className="flex items-center gap-6">
        <div className="text-sm text-gray-500 whitespace-nowrap">
          {hideTotalCount
            ? `Showing ${startItem} to ${endItem} ${itemLabel}`
            : `Showing ${startItem} to ${endItem} of ${totalCount} ${itemLabel}`
          }
        </div>

        {!hidePageSizeSelector && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 whitespace-nowrap">Rows per page:</span>
            <Select
              value={pageSize.toString()}
              onValueChange={handlePageSizeChange}
              disabled={isLoading}
            >
              <SelectTrigger className="h-9 w-[70px] text-sm rounded-xl border-gray-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Right side: Pagination buttons */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          aria-label="First page"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1 || isLoading}
          className="h-9 w-9 p-0 rounded-xl border-gray-200"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          aria-label="Previous page"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || isLoading}
          className="h-9 w-9 p-0 rounded-xl border-gray-200"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-2 mx-1">
          {(() => {
            const startPage = Math.max(1, currentPage - 1);
            const endPage = Math.min(totalPages, currentPage + 1);

            const showStartEllipsis = startPage > 1;
            const showEndEllipsis = endPage < totalPages;

            const pagesBefore = startPage - 1;
            const pagesAfter = totalPages - endPage;
            const showOnlyStartEllipsis = showStartEllipsis && showEndEllipsis && pagesBefore >= pagesAfter;
            const showOnlyEndEllipsis = showStartEllipsis && showEndEllipsis && pagesAfter > pagesBefore;

            const pages: (number | 'ellipsis')[] = [];

            if (showStartEllipsis && !showOnlyEndEllipsis) {
              pages.push(1);
              pages.push('ellipsis');
            }

            for (let i = startPage; i <= endPage; i++) {
              pages.push(i);
            }

            if (showEndEllipsis && !showOnlyStartEllipsis) {
              pages.push('ellipsis');
              pages.push(totalPages);
            }

            return pages.map((page, index) => {
              if (page === 'ellipsis') {
                return (
                  <div
                    key={`ellipsis-${index}`}
                    className="flex items-center justify-center h-9 w-9"
                  >
                    <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                  </div>
                );
              }

              return (
                <Button
                  key={page}
                  variant={currentPage === page ? 'default' : 'outline'}
                  size="sm"
                  aria-current={currentPage === page ? 'page' : undefined}
                  onClick={() => handlePageChange(page)}
                  disabled={isLoading}
                  className={cn(
                    'h-9 min-w-[2.25rem] px-3 text-sm rounded-xl transition-all border-gray-200',
                    currentPage === page
                      ? 'bg-[#1AB169] text-white hover:bg-[#1AB169]/90 border-transparent shadow-sm'
                      : 'hover:bg-gray-50 text-gray-700'
                  )}
                >
                  {page}
                </Button>
              );
            });
          })()}
        </div>

        <Button
          variant="outline"
          size="icon"
          aria-label="Next page"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || isLoading}
          className="h-9 w-9 p-0 rounded-xl border-gray-200"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          aria-label="Last page"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages || isLoading}
          className="h-9 w-9 p-0 rounded-xl border-gray-200"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
